import { fadeInOut } from '../../animation';
import clsx from 'clsx';
import { AnimatePresence, motion, useIsPresent } from 'framer-motion';
import { ComponentPropsWithoutRef, Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { SideOverContextProvider } from './SideOver.context';
import { useSideOver, UseSideOverArgs } from './hooks';
import { Portal } from '../Portal';
import { FloatingOverlay } from '@floating-ui/react';
import { useHotkeys } from 'react-hotkeys-hook';

export interface SideOverProps extends Pick<ComponentPropsWithoutRef<'div'>, 'className'>, UseSideOverArgs {
  afterEnter?: () => void;
  children: ReactNode;
  onExitComplete?: () => void;
  escShortcutDisabled?: boolean;
}

interface SideOverContentProps extends Pick<SideOverProps, 'afterEnter' | 'children' | 'className'> {
  closeSideOver: () => void;
  innerExitRoute?: string;
  returnRoute?: string;
  scrollContainerScrollTop: number;
  setInnerExitRoute: Dispatch<SetStateAction<string | undefined>>;
  setReturnRoute: Dispatch<SetStateAction<string | undefined>>;
  setScrollContainerScrollTop: Dispatch<SetStateAction<number>>;
}

const SideOverContent: FC<SideOverContentProps> = ({
  afterEnter,
  children,
  className,
  closeSideOver,
  innerExitRoute,
  returnRoute,
  scrollContainerScrollTop,
  setInnerExitRoute,
  setReturnRoute,
  setScrollContainerScrollTop,
}) => {
  const isPresent = useIsPresent();

  return (
    <motion.div className={clsx('fixed inset-0 z-20', !isPresent && 'pointer-events-none')}>
      <motion.div
        {...fadeInOut}
        onClick={() => closeSideOver()}
        transition={{ ease: 'easeInOut', duration: 0.5 }}
        className={clsx(
          'absolute inset-0 bg-background-900/40 bg-opacity-75 transition-opacity',
          !isPresent && 'pointer-events-none'
        )}
      />
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <motion.div
          onAnimationComplete={afterEnter}
          transition={{ type: 'spring', bounce: 0, duration: 1 }}
          exit={{ x: '100%' }}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          className="w-screen h-dvh md:w-[680px] bg-lightMode-snow dark:bg-darkMode-gray-dark shadow-xl flex flex-col"
        >
          <motion.div
            layoutScroll
            onScroll={(e) => {
              setScrollContainerScrollTop((e.target as HTMLDivElement).scrollTop);
            }}
            style={{
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
            }}
            className={clsx(className, 'flex-1 px-6 sm:px-12 pb-12')}
          >
            <SideOverContextProvider
              returnRoute={returnRoute}
              exitRoute={innerExitRoute}
              closeSideOver={closeSideOver}
              setReturnRoute={setReturnRoute}
              setExitRoute={setInnerExitRoute}
              scrollContainerScrollTop={scrollContainerScrollTop}
            >
              <div className="relative flex-1">{children}</div>
            </SideOverContextProvider>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export const SideOver: FC<SideOverProps> = ({
  children,
  className,
  afterEnter,
  onExitComplete,
  escShortcutDisabled = false,
  ...useSideOverArgs
}) => {
  const {
    innerAfterLeave,
    open,
    closeSideOver,
    innerExitRoute,
    returnRoute,
    setInnerExitRoute,
    setReturnRoute,
    setScrollContainerScrollTop,
    scrollContainerScrollTop,
  } = useSideOver({ ...useSideOverArgs });

  useHotkeys(
    'escape',
    () => {
      closeSideOver();
    },
    { enabled: !escShortcutDisabled, enableOnFormTags: true }
  );

  return (
    <FloatingOverlay lockScroll={open} className={open ? undefined : 'pointer-events-none'}>
      <Portal>
        <AnimatePresence
          onExitComplete={() => {
            innerAfterLeave();
            onExitComplete?.();
          }}
        >
          {open && (
            <SideOverContent
              afterEnter={afterEnter}
              className={className}
              closeSideOver={closeSideOver}
              innerExitRoute={innerExitRoute}
              returnRoute={returnRoute}
              scrollContainerScrollTop={scrollContainerScrollTop}
              setInnerExitRoute={setInnerExitRoute}
              setReturnRoute={setReturnRoute}
              setScrollContainerScrollTop={setScrollContainerScrollTop}
            >
              {children}
            </SideOverContent>
          )}
        </AnimatePresence>
      </Portal>
    </FloatingOverlay>
  );
};
